<template>
  <div :class="['notification__overlay', { active: show }]" @click.self="emit('close')">
    <transition name="slide-right-modal">
      <div v-if="show" class="notification">
        <button class="notification-modal__close" @click="emit('close')">×</button>
        <div class="notification-title__wrapper">
          <div class="notification-title__wrapper-bg">
            <h2 class="notification-title">Уведомления</h2>
            <p v-if="count" class="notification-counter">{{ count }}</p>
          </div>
          <button
            v-if="hasUnread"
            v-access="'notice:read'"
            type="button"
            class="notification-read-all"
            @click="emit('read-all')"
          >
            <IconComponent name="check-check" />
            Прочитать все
          </button>
        </div>
        <div class="notification-list">
          <LoadingComponent v-if="loading && !notifications.length" title="Загрузка..." />
          <template v-else-if="hasNotifications">
            <div
              v-for="n in notifications"
              :key="n.id"
              class="notification-item"
              :class="{ active: !n.read_at }"
            >
              <span class="notification-item__icon" :class="`notification-item__icon--${iconOf(n).color}`">
                <IconComponent :name="iconOf(n).name" />
              </span>
              <span class="notification-item__text">
                <span class="notification-item__title">{{ n.title || typeTitle(n) }}</span>
                <span class="notification-item__time">{{ timeOf(n) }}</span>
                <span v-if="n.message" class="notification-item__desc">{{ n.message }}</span>
                <a
                  v-if="hasLink(n)"
                  href="#"
                  class="notification-item__link"
                  @click.prevent="emit('open', n)"
                >
                  открыть
                </a>
              </span>
              <span
                v-access="'notice:read'"
                class="notification-item__check"
                v-tooltip.left="n.read_at ? 'Прочитано' : 'Отметить прочитанным'"
                @click="!n.read_at && emit('read', n)"
              >
                <IconComponent v-if="n.read_at" name="circle-check" />
                <IconComponent v-else name="circle" />
              </span>
            </div>
          </template>
          <template v-else>
            <p class="notification-empty">Нет новых уведомлений</p>
          </template>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
/**
 * Imports
 */
import { computed } from "vue";
import { LoadingComponent } from "@/components/ui";
import { useDate } from "@/composables";

/**
 * Props & Emits
 */
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  // { id, type, title, message, payload, read_at, created_at }
  notifications: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  // Количество непрочитанных
  count: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["close", "read", "read-all", "open"]);

/**
 * Variables
 */
const { formatDate } = useDate();

// Иконки по типу уведомления (см. ТЗ 5.9)
const ICONS = {
  "application.actual_changed": { name: "gauge", color: "blue", title: "Изменён фактический объём" },
  "application.return_initiated": { name: "undo-2", color: "yellow", title: "Инициирован возврат остатка" },
  "application.return_confirmed_to_base": { name: "warehouse", color: "green", title: "Возврат на базу" },
  "application.return_redirected": {
    name: "arrow-right-left",
    color: "purple",
    title: "Остаток перенаправлен",
  },
  "application.return_rejected": { name: "circle-x", color: "red", title: "Возврат отклонён" },
};

const DEFAULT_ICON = { name: "bell", color: "gray", title: "Уведомление" };

/**
 * Computed Properties
 */
const hasNotifications = computed(() => props.notifications.length > 0);
const hasUnread = computed(() => props.notifications.some((n) => !n.read_at) || props.count > 0);

/**
 * Methods
 */
const iconOf = (n) => ICONS[n.type] || DEFAULT_ICON;
const typeTitle = (n) => iconOf(n).title;
const hasLink = (n) => !!n?.payload?.application_id || String(n?.type || "").includes("return");

const timeOf = (n) => {
  if (!n.created_at) return "";
  const created = new Date(n.created_at);
  const diffMinutes = Math.round((Date.now() - created.getTime()) / 60000);
  if (Number.isNaN(diffMinutes)) return "";
  if (diffMinutes < 1) return "только что";
  if (diffMinutes < 60) return `${diffMinutes} мин назад`;
  if (diffMinutes < 24 * 60) return `${Math.floor(diffMinutes / 60)} ч назад`;
  return formatDate(n.created_at, "DD.MM.YYYY HH:mm");
};
</script>

<style scoped lang="stylus">
.notification-title {
  width max-content
}
.notification-title__wrapper-bg {
  width max-content
  position relative
}
.notification-title__wrapper {
  padding: 2rem 60px 3rem 1.5rem;
  border-bottom: 1px solid #E5E5E5;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}
.notification-read-all {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #3C68C8;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
}
.notification-read-all .icon {
  width: 18px;
  height: 18px;
}
.notification-counter {
  position absolute
  top -4px
  right -25px
  padding 2px 6px
  font-size 0.8rem
  background #C8CDD6
  color white
  border-radius 6px
}
.notification__overlay {
  position: fixed;
  z-index: 1000;
  left: 0; right: 0; top: 0; bottom: 0;
  background-color: rgba(143, 143, 143, 0.3);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s ease-in-out;
}
.notification__overlay.active {
  opacity: 1;
  pointer-events: auto;
  & .notification {
    transform: translateX(0);
  }
}
.notification {
  width: 30%;
  min-width: 600px;
  height: 100vh;
  background: white;
  box-shadow: 0 4px 32px rgba(0,0,0,0.18);
  margin-bottom: 0;
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  transform: translateX(100%);
  transition: transform 0.4s ease-in-out;
}
.slide-right-modal-enter-active,
.slide-right-modal-leave-active {
  transition: transform 0.35s cubic-bezier(.4,0,.2,1), opacity 0.25s;
}
.slide-right-modal-enter-from,
.slide-right-modal-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
.slide-right-modal-enter-to,
.slide-right-modal-leave-from {
  transform: translateX(0);
  opacity: 1;
}
.notification-modal__close {
  position: absolute;
  top: 12px;
  right: 16px;
  font-size: 2.5rem;
  cursor: pointer;
  background: none;
  border: none;
}
.notification-list {
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 140px);
  display: flex;
  flex-direction: column;
}
.notification-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  padding: 1.1rem 1.5rem;
  box-sizing: border-box;
  border-bottom: 1px solid #E5E5E5;
  position: relative;
}
.notification-item.active {
  border-left: 2px solid #E31E24;
  box-shadow: 0 2px 12px rgba(60,104,200,0.08);
}
.notification-item__icon {
  width: 36px;
  height: 36px;
  margin-right: 18px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #F6F6F8;
  color: #8F95A2;
}
.notification-item__icon .icon {
  width: 20px;
  height: 20px;
}
.notification-item__icon--blue {
  color: #3C68C8;
}
.notification-item__icon--yellow {
  color: #F8A81F;
}
.notification-item__icon--green {
  color: #398E4B;
}
.notification-item__icon--purple {
  color: #8764E4;
}
.notification-item__icon--red {
  color: #E31E24;
}
.notification-item__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.notification-item__title {
  font-size: 1.08rem;
  font-weight: 600;
  color: #111;
}
.notification-item__time {
  font-size: 0.95rem;
  color: #737373;
  margin-top: 1px;
}
.notification-item__desc {
  font-size: 1rem;
  color: #737373;
  margin-top: 1px;
}
.notification-item__link {
  font-size: 1rem;
  color: #3C68C8;
  margin-top: 2px;
  text-decoration: none;
  font-weight: 600;
  width: max-content;
}
.notification-item__check {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  color: #8F95A2;
}
.notification-empty {
  color: #888;
  text-align: center;
  margin-top: 40px;
}
</style>
